import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
  getDocs,
  getDoc,
  limit,
} from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';
import { Rock, User, Milestone, ActionItem, Issue, RockNote, Department, RockEvaluation, COLLECTION_NAMES } from '../types';
import { evaluateRock as evaluateRockAPI, checkAITerms as checkAITermsAPI, acceptAITerms as acceptAITermsAPI } from './rocks.api';
import { getOpenIssuesForRock, validateRockStatusUpdate } from './rocks.util';
import { parseDateFromInput } from '../../../utils/date.utils';

/**
 * Hook for managing Rocks data with Firestore
 * Replaces Supabase queries with Firestore v9+ modular API
 */
export function useRocks(userId: string | undefined) {
  const [rocks, setRocks] = useState<Rock[]>([]);
  const { eosUsers = [] } = useAuth() as { eosUsers: User[] };
  const [departments, setDepartments] = useState<Department[]>([]);
  const [rockEvaluations, setRockEvaluations] = useState<RockEvaluation[]>([]);
  const [rockAiScores, setRockAiScores] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  // Load departments once (excluding deleted)
  useEffect(() => {
    const departmentsQuery = query(
      collection(db, COLLECTION_NAMES.DEPARTMENTS),
      orderBy('name', 'asc')
    );
    const unsubscribe = onSnapshot(departmentsQuery, (snapshot) => {
      const departmentsData = (snapshot.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Department[])
        // Filter out soft-deleted departments
        .filter((dept) => !dept.deletedAt);
      setDepartments(departmentsData);
    }, (error) => {
      console.error('Error loading departments:', error);
      // Don't fail if departments collection doesn't exist yet
    });

    return () => unsubscribe();
  }, []);

  // Load rocks with real-time listener
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    // Query for active rocks (not achieved, not dropped)
    const rocksQuery = query(
      collection(db, COLLECTION_NAMES.ROCKS),
      where('isAchieved', '==', false),
      where('isDropped', '==', false),
      orderBy('dueDate', 'asc')
    );

    const unsubscribe = onSnapshot(
      rocksQuery,
      (snapshot) => {
        const rocksData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            // Convert Firestore Timestamps to Date objects
            dueDate: data.dueDate?.toDate?.() || data.dueDate,
            createdAt: data.createdAt || Timestamp.now(),
            updatedAt: data.updatedAt || Timestamp.now(),
            achievedAt: data.achievedAt?.toDate?.() || data.achievedAt,
            droppedAt: data.droppedAt?.toDate?.() || data.droppedAt,
          } as Rock;
        });
        setRocks(rocksData);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading rocks:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  // Load rock evaluations with real-time listener
  useEffect(() => {
    const evaluationsQuery = query(
      collection(db, COLLECTION_NAMES.ROCK_EVALUATIONS),
      orderBy('evaluatedAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      evaluationsQuery,
      (snapshot) => {
        const evaluationsData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            evaluatedAt: data.evaluatedAt || Timestamp.now(),
            createdAt: data.createdAt || Timestamp.now(),
          } as RockEvaluation;
        });
        setRockEvaluations(evaluationsData);

        // Create a map of rockId -> overallScore for quick lookup
        // Get the most recent evaluation for each rock
        const scoresMap: Record<string, number> = {};
        const rockEvalMap = new Map<string, RockEvaluation>();
        
        evaluationsData.forEach((evaluation) => {
          const existing = rockEvalMap.get(evaluation.rockId);
          if (!existing) {
            rockEvalMap.set(evaluation.rockId, evaluation);
          } else {
            // Compare timestamps - convert to comparable format
            const getTimestampMillis = (ts: Timestamp | Date | string): number => {
              if (ts instanceof Timestamp) {
                return ts.toMillis();
              }
              if (ts instanceof Date) {
                return ts.getTime();
              }
              return 0;
            };
            
            const existingTime = getTimestampMillis(existing.evaluatedAt);
            const evalTime = getTimestampMillis(evaluation.evaluatedAt);
            
            if (evalTime > existingTime) {
              rockEvalMap.set(evaluation.rockId, evaluation);
            }
          }
        });
        
        rockEvalMap.forEach((evaluation, rockId) => {
          scoresMap[rockId] = evaluation.overallScore;
        });
        
        setRockAiScores(scoresMap);
      },
      (error) => {
        console.error('Error loading rock evaluations:', error);
      }
    );

    return () => unsubscribe();
  }, []);

  const createRock = useCallback(async (formData: {
    rock: string;
    measurable: string;
    dueDate: string;
    ownerId?: string;
    status?: 'on-track' | 'off-track';
    progress?: number;
    notes?: string;
    collaborators?: string[];
    rockType?: 'individual' | 'company';
    departmentId?: string; // Optional department assignment
    milestones?: Array<{ task: string; dueDate: string }>;
    actionItems?: Array<{ task: string; assigneeId?: string; dueDate?: string }>;
  }) => {
    if (!userId) return;

    try {
      const now = Timestamp.now();
      const dueDateValue = parseDateFromInput(formData.dueDate);

      // Create the rock document
      const rockData = {
        ownerId: formData.ownerId || userId,
        collaborators: formData.collaborators || [],
        rock: formData.rock,
        measurable: formData.measurable,
        status: formData.status || 'on-track',
        progress: formData.progress || 0,
        weekNumber: 1, // Default, can be calculated later
        dueDate: dueDateValue,
        notes: formData.notes || '',
        isAchieved: false,
        isDropped: false,
        rockType: formData.rockType || 'individual',
        departmentId: formData.departmentId || null, // Optional department assignment
        createdAt: now,
        updatedAt: now,
      };

      const rockRef = await addDoc(collection(db, COLLECTION_NAMES.ROCKS), rockData);
      const rockId = rockRef.id;

      // Create milestones if provided
      if (formData.milestones && formData.milestones.length > 0) {
        const milestonesPromises = formData.milestones.map((milestone, index) => {
          return addDoc(collection(db, COLLECTION_NAMES.MILESTONES), {
            rockId: rockId,
            task: milestone.task,
            dueDate: parseDateFromInput(milestone.dueDate),
            complete: false,
            sortOrder: index,
            createdAt: now,
          });
        });
        await Promise.all(milestonesPromises);
      }

      // Create action items if provided
      if (formData.actionItems && formData.actionItems.length > 0) {
        
        const actionItemsPromises = formData.actionItems.map((item: { task: string; assigneeId?: string; dueDate?: string }) => {
          // Default action item due date to 7 days from now, or the rock's due date if sooner
          // BUT if item.dueDate is provided, usage that instead!
          let actionItemDueDate = new Date();
          
          if (item.dueDate) {
            actionItemDueDate = parseDateFromInput(item.dueDate);
          } else {
             actionItemDueDate.setDate(actionItemDueDate.getDate() + 7);
             // If rock due date is sooner than 7 days, cap it there
             if (dueDateValue < actionItemDueDate) {
               actionItemDueDate.setTime(dueDateValue.getTime());
             }
          }

          return addDoc(collection(db, COLLECTION_NAMES.ACTION_ITEMS), {
            rockId: rockId,
            // Map 'task' (from form) to 'description' (schema)
            task: item.task, // Keep legacy field for safety
            description: item.task,
            // Map 'assigneeId' (from form) to 'assignedToUserId' (schema)
            assigneeId: item.assigneeId || null, 
            assignedToUserId: item.assigneeId || null,
            // Owner is the rock owner by default
            ownerId: formData.ownerId || userId,
            // Metadata
            complete: false,
            dropped: false,
            createdAt: now,
            updatedAt: now,
            createdByUserId: userId || '',
            // Set default due date
            dueDate: actionItemDueDate,
          });
        });
        await Promise.all(actionItemsPromises);
      }

      // Return the rockRef so the ID can be used
      return rockRef;
    } catch (error) {
      console.error('Error creating rock:', error);
      throw error;
    }
  }, [userId]);

  const updateRock = useCallback(async (
    rockId: string,
    formData: Partial<{
      rock: string;
      measurable: string;
      dueDate: string | Date;
      ownerId: string;
      status: 'on-track' | 'off-track';
      progress: number;
      notes: string;
      collaborators: string[];
      rockType: 'individual' | 'company';
      departmentId?: string; // Optional department assignment
    }>
  ) => {
    try {
      // Validate status update if status is being changed
      if (formData.status) {
        const validation = await validateRockStatusUpdate(rockId, formData.status);
        if (!validation.valid) {
          throw new Error(validation.error || 'Cannot update rock status');
        }
      }

      const rockRef = doc(db, COLLECTION_NAMES.ROCKS, rockId);
      const updateData: any = {
        ...formData,
        updatedAt: Timestamp.now(),
      };

      // Add updatedByUserId if userId is available
      if (userId) {
        updateData.updatedByUserId = userId;
      }

      // Convert dueDate to Date if it's a string
      if (updateData.dueDate && typeof updateData.dueDate === 'string') {
        updateData.dueDate = parseDateFromInput(updateData.dueDate);
      }

      // Filter out undefined values - Firestore doesn't allow undefined
      const cleanedUpdateData: any = {};
      Object.keys(updateData).forEach((key) => {
        if (updateData[key] !== undefined) {
          cleanedUpdateData[key] = updateData[key];
        }
      });

      await updateDoc(rockRef, cleanedUpdateData);

      // TODO: Send notifications (implement notification system)
    } catch (error) {
      console.error('Error updating rock:', error);
      throw error;
    }
  }, [userId]);

  const deleteRock = useCallback(async (rockId: string) => {
    try {
      const rockRef = doc(db, COLLECTION_NAMES.ROCKS, rockId);
      await deleteDoc(rockRef);
    } catch (error) {
      console.error('Error deleting rock:', error);
      throw error;
    }
  }, []);

  const achieveRock = useCallback(async (rockId: string) => {
    try {
      const rockRef = doc(db, COLLECTION_NAMES.ROCKS, rockId);
      const updateData: any = {
        isAchieved: true,
        achievedAt: Timestamp.now(),
        progress: 100,
        status: 'on-track',
        updatedAt: Timestamp.now(),
      };

      // Add updatedByUserId if userId is available
      if (userId) {
        updateData.updatedByUserId = userId;
      }

      await updateDoc(rockRef, updateData);
    } catch (error) {
      console.error('Error achieving rock:', error);
      throw error;
    }
  }, [userId]);

  const dropRock = useCallback(async (rockId: string) => {
    try {
      const rockRef = doc(db, COLLECTION_NAMES.ROCKS, rockId);
      const updateData: any = {
        isDropped: true,
        droppedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      // Add updatedByUserId if userId is available
      if (userId) {
        updateData.updatedByUserId = userId;
      }

      await updateDoc(rockRef, updateData);
    } catch (error) {
      console.error('Error dropping rock:', error);
      throw error;
    }
  }, [userId]);

  /**
   * Load milestones for multiple rocks
   * Handles Firestore array 'in' query limitation (max 10 items)
   * @param rockIds - Array of rock IDs
   * @returns Map of rockId to Milestone[]
   */
  const loadMilestonesForRocks = useCallback(async (rockIds: string[]): Promise<Map<string, Milestone[]>> => {
    const milestonesMap = new Map<string, Milestone[]>();

    if (rockIds.length === 0) return milestonesMap;

    // Firestore 'in' queries are limited to 10 items, so we need to batch
    const batchSize = 10;
    const batches: string[][] = [];

    for (let i = 0; i < rockIds.length; i += batchSize) {
      batches.push(rockIds.slice(i, i + batchSize));
    }

    try {
      const promises = batches.map(async (batch) => {
        const milestonesQuery = query(
          collection(db, COLLECTION_NAMES.MILESTONES),
          where('rockId', 'in', batch)
        );
        const snapshot = await getDocs(milestonesQuery);
        return snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          dueDate: doc.data().dueDate?.toDate?.() || doc.data().dueDate,
          createdAt: doc.data().createdAt || Timestamp.now(),
        })) as Milestone[];
      });

      const results = await Promise.all(promises);
      const allMilestones = results.flat();

      // Group milestones by rockId
      allMilestones.forEach((milestone) => {
        if (!milestonesMap.has(milestone.rockId)) {
          milestonesMap.set(milestone.rockId, []);
        }
        milestonesMap.get(milestone.rockId)!.push(milestone);
      });
    } catch (error) {
      console.error('Error loading milestones:', error);
    }

    return milestonesMap;
  }, []);

  /**
   * Load action items for multiple rocks
   * Handles Firestore array 'in' query limitation (max 10 items)
   * @param rockIds - Array of rock IDs
   * @returns Map of rockId to ActionItem[]
   */
  const loadActionItemsForRocks = useCallback(async (rockIds: string[]): Promise<Map<string, ActionItem[]>> => {
    const actionItemsMap = new Map<string, ActionItem[]>();

    if (rockIds.length === 0) return actionItemsMap;

    // Firestore 'in' queries are limited to 10 items, so we need to batch
    const batchSize = 10;
    const batches: string[][] = [];

    for (let i = 0; i < rockIds.length; i += batchSize) {
      batches.push(rockIds.slice(i, i + batchSize));
    }

    try {
      const promises = batches.map(async (batch) => {
        const actionItemsQuery = query(
          collection(db, COLLECTION_NAMES.ACTION_ITEMS),
          where('rockId', 'in', batch)
        );
        const snapshot = await getDocs(actionItemsQuery);
        return snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt || Timestamp.now(),
        })) as ActionItem[];
      });

      const results = await Promise.all(promises);
      const allActionItems = results.flat();

      // Group action items by rockId
      allActionItems.forEach((actionItem) => {
        if (!actionItemsMap.has(actionItem.rockId)) {
          actionItemsMap.set(actionItem.rockId, []);
        }
        actionItemsMap.get(actionItem.rockId)!.push(actionItem);
      });
    } catch (error) {
      console.error('Error loading action items:', error);
    }

    return actionItemsMap;
  }, []);

  /**
   * Load issues for multiple rocks
   * Handles Firestore array 'in' query limitation (max 10 items)
   * @param rockIds - Array of rock IDs
   * @returns Array of issues related to the rocks
   */
  const loadIssuesForRocks = useCallback(async (rockIds: string[]): Promise<Issue[]> => {
    if (rockIds.length === 0) return [];

    // Firestore 'in' queries are limited to 10 items, so we need to batch
    const batchSize = 10;
    const batches: string[][] = [];

    for (let i = 0; i < rockIds.length; i += batchSize) {
      batches.push(rockIds.slice(i, i + batchSize));
    }

    try {
      const promises = batches.map(async (batch) => {
        // Note: Firestore doesn't support != operator with 'in', so we'll filter client-side
        const issuesQuery = query(
          collection(db, COLLECTION_NAMES.ISSUES),
          where('isRock', '==', true),
          where('relatedRockId', 'in', batch)
        );
        const snapshot = await getDocs(issuesQuery);
        return snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt || Timestamp.now(),
            updatedAt: data.updatedAt || Timestamp.now(),
            solvedAt: data.solvedAt?.toDate?.() || data.solvedAt,
            firstOpenedDate: data.firstOpenedDate || Timestamp.now(),
            createdInMeetingId: data.createdInMeetingId || undefined,
          } as Issue;
        });
      });

      const results = await Promise.all(promises);
      const allIssues = results.flat();

      // Filter out solved issues client-side (since Firestore doesn't support != with 'in')
      return allIssues.filter((issue) => issue.status !== 'solved');
    } catch (error) {
      console.error('Error loading issues:', error);
      return [];
    }
  }, []);

  /**
   * Set up real-time subscription for issues related to rocks
   * @param rockIds - Array of rock IDs to monitor
   * @param callback - Callback function to call when issues change
   * @returns Unsubscribe function
   */
  const subscribeToIssues = useCallback((
    rockIds: string[],
    callback: (issues: Issue[]) => void
  ): (() => void) => {
    if (rockIds.length === 0) {
      callback([]);
      return () => {}; // Return no-op unsubscribe
    }

    // For real-time subscriptions, we'll listen to all rock issues and filter client-side
    // since Firestore real-time doesn't support complex queries with 'in' and '!='
    const issuesQuery = query(
      collection(db, COLLECTION_NAMES.ISSUES),
      where('isRock', '==', true)
    );

    const unsubscribe = onSnapshot(
      issuesQuery,
      (snapshot) => {
        const issues = snapshot.docs
          .map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              createdAt: data.createdAt || Timestamp.now(),
              updatedAt: data.updatedAt || Timestamp.now(),
              solvedAt: data.solvedAt?.toDate?.() || data.solvedAt,
              firstOpenedDate: data.firstOpenedDate || Timestamp.now(),
              createdInMeetingId: data.createdInMeetingId || undefined,
            } as Issue;
          })
          .filter(
            (issue) =>
              issue.status !== 'solved' &&
              issue.relatedRockId &&
              rockIds.includes(issue.relatedRockId)
          );
        callback(issues);
      },
      (error) => {
        console.error('Error in issues subscription:', error);
      }
    );

    return unsubscribe;
  }, []);

  /**
   * Set up real-time subscription for action items related to rocks
   * @param rockIds - Array of rock IDs to monitor
   * @param callback - Callback function to call when action items change
   * @returns Unsubscribe function
   */
  const subscribeToActionItems = useCallback((
    rockIds: string[],
    callback: (actionItems: ActionItem[]) => void
  ): (() => void) => {
    if (rockIds.length === 0) {
      callback([]);
      return () => {}; // Return no-op unsubscribe
    }

    // Listen to all action items and filter client-side
    const actionItemsQuery = collection(db, COLLECTION_NAMES.ACTION_ITEMS);

    const unsubscribe = onSnapshot(
      actionItemsQuery,
      (snapshot) => {
        const actionItems = snapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt || Timestamp.now(),
          }))
          .filter((item: any) => item.rockId && rockIds.includes(item.rockId)) as ActionItem[];
        callback(actionItems);
      },
      (error) => {
        console.error('Error in action items subscription:', error);
      }
    );

    return unsubscribe;
  }, []);

  /**
   * Set up real-time subscription for milestones for a single rock
   * @param rockId - ID of the rock to monitor
   * @param callback - Callback function to call when milestones change
   * @returns Unsubscribe function
   */
  const subscribeToMilestonesForRock = useCallback((
    rockId: string,
    callback: (milestones: Milestone[]) => void
  ): (() => void) => {
    if (!rockId) {
      callback([]);
      return () => {};
    }

    const milestonesQuery = query(
      collection(db, COLLECTION_NAMES.MILESTONES),
      where('rockId', '==', rockId),
      orderBy('sortOrder', 'asc')
    );

    return onSnapshot(
      milestonesQuery,
      (snapshot) => {
        const milestones = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          dueDate: doc.data().dueDate?.toDate?.() || doc.data().dueDate,
          createdAt: doc.data().createdAt || Timestamp.now(),
        })) as Milestone[];
        callback(milestones);
      },
      (error) => {
        console.error('Error in milestones subscription:', error);
      }
    );
  }, []);

  /**
   * Set up real-time subscription for action items for a single rock
   * @param rockId - ID of the rock to monitor
   * @param callback - Callback function to call when action items change
   * @returns Unsubscribe function
   */
  const subscribeToActionItemsForRock = useCallback((
    rockId: string,
    callback: (actionItems: ActionItem[]) => void
  ): (() => void) => {
    if (!rockId) {
      callback([]);
      return () => {};
    }

    // Direct query for this rock's action items
    const actionItemsQuery = query(
      collection(db, COLLECTION_NAMES.ACTION_ITEMS),
      where('rockId', '==', rockId)
    );

    return onSnapshot(
      actionItemsQuery,
      (snapshot) => {
        const actionItems = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt || Timestamp.now(),
        })) as ActionItem[];
        callback(actionItems);
      },
      (error) => {
        console.error('Error in action items subscription:', error);
      }
    );
  }, []);

  /**
   * Add an issue to a rock
   */
  const addIssue = useCallback(async (
    rockId: string,
    name: string,
    description: string,
    ownerId: string
  ) => {
    try {
      const now = Timestamp.now();
      await addDoc(collection(db, COLLECTION_NAMES.ISSUES), {
        relatedRockId: rockId,
        name,
        description,
        isRock: true,
        status: 'open',
        priority: 'medium',
        ownerId,
        firstOpenedDate: now,
        createdAt: now,
        updatedAt: now,
      });
    } catch (error) {
      console.error('Error adding issue:', error);
      throw error;
    }
  }, []);

  /**
   * Resolve an issue
   * Validates that all linked action items are complete before resolving
   * Returns metadata about whether this is the last issue for the related rock
   */
  const resolveIssue = useCallback(async (
    issueId: string,
    resolutionNotes?: string
  ): Promise<{ isLastIssue: boolean; relatedRockId?: string }> => {
    try {
      // Get the issue first to check its relatedRockId
      const issueRef = doc(db, COLLECTION_NAMES.ISSUES, issueId);
      const issueDoc = await getDoc(issueRef);
      
      if (!issueDoc.exists()) {
        throw new Error('Issue not found');
      }

      const issueData = issueDoc.data() as Issue;
      const relatedRockId = issueData.relatedRockId;

      // Check for incomplete action items linked to this issue
      const actionItemsQuery = query(
        collection(db, COLLECTION_NAMES.ACTION_ITEMS),
        where('linkedIssueId', '==', issueId),
        where('complete', '==', false),
        where('dropped', '==', false)
      );
      const actionItemsSnapshot = await getDocs(actionItemsQuery);

      if (!actionItemsSnapshot.empty) {
        const incompleteActionItems = actionItemsSnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            description: data.description || data.task || 'Untitled Action Item',
          };
        });
        const actionItemList = incompleteActionItems.map((t) => `- ${t.description}`).join('\n');
        const errorMessage = `Cannot resolve this issue. The following action items are still incomplete:\n\n${actionItemList}\n\nPlease complete or drop these action items before resolving the issue.`;
        throw new Error(errorMessage);
      }

      // Check if this is the last open issue for the related rock BEFORE resolving
      let isLastIssue = false;
      if (relatedRockId) {
        const openIssues = await getOpenIssuesForRock(relatedRockId);
        // Filter out the current issue we're about to resolve
        const otherOpenIssues = openIssues.filter((issue) => issue.id !== issueId);
        isLastIssue = otherOpenIssues.length === 0;
      }

      // If it's the last issue, return metadata without resolving
      // The parent component will handle showing the ResolveIssueModal with isLastIssue prop
      if (isLastIssue) {
        return {
          isLastIssue: true,
          relatedRockId: relatedRockId || undefined,
        };
      }

      // Not the last issue - proceed with resolution
      const updateData: any = {
        status: 'solved',
        solvedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      if (resolutionNotes) {
        updateData.resolutionNotes = resolutionNotes;
      }

      // Add resolvedByUserId if userId is available
      if (userId) {
        updateData.resolvedByUserId = userId;
      }

      await updateDoc(issueRef, updateData);

      return {
        isLastIssue: false,
        relatedRockId: relatedRockId || undefined,
      };
    } catch (error) {
      console.error('Error resolving issue:', error);
      throw error;
    }
  }, [userId]);

  /**
   * Actually resolve an issue (without checking for last issue)
   * Used by ResolveIssueModal after user confirms action for the last issue
   */
  const actuallyResolveIssue = useCallback(async (
    issueId: string,
    resolutionNotes?: string
  ): Promise<void> => {
    try {
      // Get the issue first
      const issueRef = doc(db, COLLECTION_NAMES.ISSUES, issueId);
      const issueDoc = await getDoc(issueRef);
      
      if (!issueDoc.exists()) {
        throw new Error('Issue not found');
      }

      // Check for incomplete todos linked to this issue
      const actionItemsQuery = query(
        collection(db, COLLECTION_NAMES.ACTION_ITEMS),
        where('linkedIssueId', '==', issueId),
        where('complete', '==', false),
        where('dropped', '==', false)
      );
      const actionItemsSnapshot = await getDocs(actionItemsQuery);

      if (!actionItemsSnapshot.empty) {
        const incompleteActionItems = actionItemsSnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            description: data.description || data.task || 'Untitled Action Item',
          };
        });
        const actionItemList = incompleteActionItems.map((t) => `- ${t.description}`).join('\n');
        const errorMessage = `Cannot resolve this issue. The following action items are still incomplete:\n\n${actionItemList}\n\nPlease complete or drop these action items before resolving the issue.`;
        throw new Error(errorMessage);
      }

      // Update issue with resolution
      const updateData: any = {
        status: 'solved',
        solvedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      if (resolutionNotes) {
        updateData.resolutionNotes = resolutionNotes;
      }

      // Add resolvedByUserId if userId is available
      if (userId) {
        updateData.resolvedByUserId = userId;
      }

      await updateDoc(issueRef, updateData);
    } catch (error) {
      console.error('Error resolving issue:', error);
      throw error;
    }
  }, [userId]);

  /**
   * Update an issue's name and/or description
   */
  const updateIssue = useCallback(async (
    issueId: string,
    name: string,
    description: string
  ) => {
    try {
      const issueRef = doc(db, COLLECTION_NAMES.ISSUES, issueId);
      await updateDoc(issueRef, {
        name: name,
        description: description,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error updating issue:', error);
      throw error;
    }
  }, []);

  /**
   * Toggle action item completion
   */
  const toggleActionItem = useCallback(async (actionItemId: string, complete: boolean) => {
    try {
      const actionItemRef = doc(db, COLLECTION_NAMES.ACTION_ITEMS, actionItemId);
      await updateDoc(actionItemRef, {
        complete,
      });
    } catch (error) {
      console.error('Error toggling action item:', error);
      throw error;
    }
  }, []);

  /**
   * Add an action item to a rock
   */
  const addActionItem = useCallback(async (
    rockId: string,
    task: string,
    assigneeId: string | null,
    dueDate: string,
    issueId?: string
  ) => {
    try {
      const now = Timestamp.now();
      // Parse due date - handle both Date strings and ISO strings
      let dueDateValue: Date;
      if (dueDate.includes('T')) {
        dueDateValue = new Date(dueDate);
      } else {
        // If no time component, append T12:00:00 to avoid timezone shift
        dueDateValue = new Date(`${dueDate}T12:00:00`);
      }

      await addDoc(collection(db, COLLECTION_NAMES.ACTION_ITEMS), {
        rockId,
        task, // Legacy field for backward compatibility
        description: task, // Primary field per new schema
        assigneeId: assigneeId || null, // Legacy field
        assignedToUserId: assigneeId || null, // Primary field per new schema
        issueId: issueId || null, // Legacy field
        linkedIssueId: issueId || null, // Primary field per new schema
        dueDate: dueDateValue,
        complete: false,
        dropped: false,
        createdAt: now,
        updatedAt: now,
        createdByUserId: userId || '',
      });
    } catch (error) {
      console.error('Error adding action item:', error);
      throw error;
    }

  }, [userId]);

  /**
   * Update an action item's description, assignee, and/or due date
   */
  const updateActionItem = useCallback(async (
    actionItemId: string,
    task: string,
    assigneeId: string | null,
    dueDate: string
  ) => {
    try {
      // Parse due date - handle both Date strings and ISO strings
      let dueDateValue: Date;
      if (dueDate.includes('T')) {
        dueDateValue = new Date(dueDate);
      } else {
        // If no time component, append T12:00:00 to avoid timezone shift
        dueDateValue = new Date(`${dueDate}T12:00:00`);
      }

      const actionItemRef = doc(db, COLLECTION_NAMES.ACTION_ITEMS, actionItemId);
      await updateDoc(actionItemRef, {
        task: task, // Legacy field
        description: task, // Primary field
        assigneeId: assigneeId || null, // Legacy field
        assignedToUserId: assigneeId || null, // Primary field
        dueDate: dueDateValue,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error updating action item:', error);
      throw error;
    }
  }, []);
  /**
   * Load issues for a single rock
   */
  const loadIssuesForRock = useCallback(async (rockId: string): Promise<Issue[]> => {
    try {
      const issuesQuery = query(
        collection(db, COLLECTION_NAMES.ISSUES),
        where('relatedRockId', '==', rockId),
        where('isRock', '==', true)
      );
      const snapshot = await getDocs(issuesQuery);
      return snapshot.docs
        .map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt || Timestamp.now(),
            updatedAt: data.updatedAt || Timestamp.now(),
            solvedAt: data.solvedAt?.toDate?.() || data.solvedAt,
            firstOpenedDate: data.firstOpenedDate || Timestamp.now(),
            createdInMeetingId: data.createdInMeetingId || undefined,
          } as Issue;
        });
      // Note: We return ALL issues (including solved) so the component can filter them
      // The component separates open/in-progress from solved issues for display
    } catch (error) {
      console.error('Error loading issues for rock:', error);
      return [];
    }
  }, []);

  /**
   * Load rock action items for a single rock
   */
  const loadRockActionItemsForRock = useCallback(async (rockId: string): Promise<ActionItem[]> => {
    try {
      const actionItemsQuery = query(
        collection(db, COLLECTION_NAMES.ACTION_ITEMS),
        where('rockId', '==', rockId)
      );
      const snapshot = await getDocs(actionItemsQuery);
      return snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Ensure description exists (use task as fallback for legacy data)
          description: data.description || data.task || '',
          dueDate: data.dueDate?.toDate?.() || data.dueDate,
          completedDate: data.completedDate?.toDate?.() || data.completedDate,
          createdAt: data.createdAt || Timestamp.now(),
          updatedAt: data.updatedAt || Timestamp.now(),
        } as ActionItem;
      });
    } catch (error) {
      console.error('Error loading action items for rock:', error);
      return [];
    }
  }, []);

  /**
   * Load milestones for a single rock
   */
  const loadMilestonesForRock = useCallback(async (rockId: string): Promise<Milestone[]> => {
    try {
      const milestonesQuery = query(
        collection(db, COLLECTION_NAMES.MILESTONES),
        where('rockId', '==', rockId),
        orderBy('sortOrder', 'asc')
      );
      const snapshot = await getDocs(milestonesQuery);
      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        dueDate: doc.data().dueDate?.toDate?.() || doc.data().dueDate,
        createdAt: doc.data().createdAt || Timestamp.now(),
      })) as Milestone[];
    } catch (error) {
      console.error('Error loading milestones for rock:', error);
      return [];
    }
  }, []);

  /**
   * Add a milestone to a rock
   */
  const addMilestone = useCallback(async (
    rockId: string,
    task: string,
    dueDate: string,
    sortOrder: number
  ) => {
    try {
      const now = Timestamp.now();
      await addDoc(collection(db, COLLECTION_NAMES.MILESTONES), {
        rockId: rockId,
        task: task,
        dueDate: parseDateFromInput(dueDate), // Ensure this is a Date object
        complete: false,
        sortOrder: sortOrder,
        createdAt: now,
      });
    } catch (error) {
      console.error('Error adding milestone:', error);
      throw error;
    }
  }, []);

  /**
   * Toggle milestone completion status
   */
  const toggleMilestone = useCallback(async (milestoneId: string, complete: boolean) => {
    try {
      const milestoneRef = doc(db, COLLECTION_NAMES.MILESTONES, milestoneId);
      await updateDoc(milestoneRef, {
        complete: complete,
      });
    } catch (error) {
      console.error('Error toggling milestone:', error);
      throw error;
    }
  }, []);

  /**
   * Update a milestone's task and/or due date
   */
  const updateMilestone = useCallback(async (
    milestoneId: string,
    task: string,
    dueDate: string
  ) => {
    try {
      const milestoneRef = doc(db, COLLECTION_NAMES.MILESTONES, milestoneId);
      await updateDoc(milestoneRef, {
        task: task,
        dueDate: parseDateFromInput(dueDate),
      });
    } catch (error) {
      console.error('Error updating milestone:', error);
      throw error;
    }
  }, []);

  /**
   * Load action items for a single rock (both rock-specific and linked)
   */
  const loadActionItemsForRock = useCallback(async (rockId: string): Promise<ActionItem[]> => {
    try {
      // Query for both rock-specific (rockId) and linked (linkedRockId) action items
      const rockSpecificQuery = query(
        collection(db, COLLECTION_NAMES.ACTION_ITEMS),
        where('rockId', '==', rockId),
        where('dropped', '==', false)
      );
      const linkedQuery = query(
        collection(db, COLLECTION_NAMES.ACTION_ITEMS),
        where('linkedRockId', '==', rockId),
        where('dropped', '==', false)
      );
      
      const [rockSpecificSnapshot, linkedSnapshot] = await Promise.all([
        getDocs(rockSpecificQuery),
        getDocs(linkedQuery)
      ]);
      
      const allActionItems = [
        ...rockSpecificSnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            // Ensure description exists (use task as fallback for legacy data)
            description: data.description || data.task || '',
            dueDate: data.dueDate?.toDate?.() || data.dueDate,
            completedDate: data.completedDate?.toDate?.() || data.completedDate,
            createdAt: data.createdAt || Timestamp.now(),
            updatedAt: data.updatedAt || Timestamp.now(),
          } as ActionItem;
        }),
        ...linkedSnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            // Ensure description exists (use task as fallback for legacy data)
            description: data.description || data.task || '',
            dueDate: data.dueDate?.toDate?.() || data.dueDate,
            completedDate: data.completedDate?.toDate?.() || data.completedDate,
            createdAt: data.createdAt || Timestamp.now(),
            updatedAt: data.updatedAt || Timestamp.now(),
          } as ActionItem;
        })
      ];
      
      // Remove duplicates and sort by dueDate
      const uniqueActionItems = Array.from(
        new Map(allActionItems.map(item => [item.id, item])).values()
      );
      
      return uniqueActionItems.sort((a, b) => {
        const dateA = a.dueDate ? new Date(a.dueDate).getTime() : 0;
        const dateB = b.dueDate ? new Date(b.dueDate).getTime() : 0;
        return dateA - dateB;
      });
    } catch (error) {
      console.error('Error loading action items for rock:', error);
      return [];
    }
  }, []);

  /**
   * Save a rock update (Latest Update)
   * Saves the note to rock_notes collection and clears the notes field on the main rock document
   */
  const saveRockUpdate = useCallback(async (rockId: string, note: string, currentUserId: string) => {
    if (!note.trim()) return; // Do not save empty notes

    try {
      const now = Timestamp.now();

      // 1. Add the note to the 'rock_notes' collection
      const noteData = {
        rockId: rockId,
        note: note,
        createdById: currentUserId,
        createdAt: now,
      };
      await addDoc(collection(db, COLLECTION_NAMES.ROCK_NOTES), noteData);

      // 2. Clear the 'notes' field on the main rock doc (as it's now archived)
      //    and update its 'updatedAt' timestamp.
      const rockRef = doc(db, COLLECTION_NAMES.ROCKS, rockId);
      await updateDoc(rockRef, {
        notes: '', // Clear the denormalized 'notes' field
        updatedAt: now,
      });
    } catch (error) {
      console.error('Error saving rock update:', error);
      throw error;
    }
  }, []);

  /**
   * Load previous notes for a rock
   * Queries the rock_notes collection for all notes related to a specific rock
   */
  const loadPreviousNotes = useCallback(async (rockId: string): Promise<RockNote[]> => {
    try {
      const notesQuery = query(
        collection(db, COLLECTION_NAMES.ROCK_NOTES),
        where('rockId', '==', rockId),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(notesQuery);

      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt || Timestamp.now(),
      })) as RockNote[];
    } catch (error) {
      console.error('Error loading previous notes:', error);
      return [];
    }
  }, []);

  /**
   * Check if user has accepted AI terms
   * Calls Firebase Cloud Function to check terms acceptance
   */
  const checkAITerms = useCallback(async (): Promise<boolean> => {
    if (!userId) return false;
    return checkAITermsAPI(userId);
  }, [userId]);

  /**
   * Accept AI terms for the current user
   * Calls Firebase Cloud Function to record terms acceptance
   */
  const acceptAITerms = useCallback(async (): Promise<boolean> => {
    if (!userId) {
      throw new Error('User ID is required to accept AI terms');
    }
    return acceptAITermsAPI(userId);
  }, [userId]);

  /**
   * Evaluate a rock using AI
   * Calls Firebase Cloud Function to evaluate the rock
   */
  const evaluateRock = useCallback(async (rockId: string): Promise<any> => {
    return evaluateRockAPI(rockId);
  }, []);

  /**
   * Get the latest evaluation for a rock
   * Queries rock_evaluations collection for the most recent evaluation
   */
  const getLatestEvaluation = useCallback(async (rockId: string): Promise<RockEvaluation | null> => {
    try {
      const evaluationsQuery = query(
        collection(db, COLLECTION_NAMES.ROCK_EVALUATIONS),
        where('rockId', '==', rockId),
        orderBy('evaluatedAt', 'desc'),
        limit(1)
      );
      const snapshot = await getDocs(evaluationsQuery);
      if (snapshot.empty) return null;
      
      const doc = snapshot.docs[0];
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        evaluatedAt: data.evaluatedAt || Timestamp.now(),
        createdAt: data.createdAt || Timestamp.now(),
      } as RockEvaluation;
    } catch (error) {
      console.error('Error getting latest evaluation:', error);
      return null;
    }
  }, []);

  /**
   * Save an evaluation result to the rock_evaluations collection
   * @param rockId - The rock ID
   * @param evaluationResult - The evaluation result from the AI
   * @param rockSnapshot - Snapshot of the rock at evaluation time
   */
  const saveEvaluation = useCallback(async (
    rockId: string,
    evaluationResult: any,
    rockSnapshot: {
      rock: string;
      measurable: string;
      due_date: Date | string;
      status: string;
      progress: number;
    }
  ) => {
    if (!userId) return;

    try {
      const now = Timestamp.now();
      const evaluationData = {
        rockId,
        overallScore: evaluationResult.overall_score || 0,
        smartScore: evaluationResult.smart_score || 0,
        clarityScore: evaluationResult.clarity_score || 0,
        scopeScore: evaluationResult.scope_score || 0,
        impactScore: evaluationResult.impact_score || 0,
        actionScore: evaluationResult.action_score || 0,
        strengths: evaluationResult.strengths || [],
        weaknesses: evaluationResult.weaknesses || [],
        recommendations: evaluationResult.recommendations || [],
        aiRewriteRock: evaluationResult.ai_rewrite_rock || null,
        aiRewriteMeasurable: evaluationResult.ai_rewrite_measurable || null,
        aiExplanation: evaluationResult.ai_explanation || null,
        evaluatedAt: now,
        evaluatedBy: userId,
        rockSnapshot: {
          rock: rockSnapshot.rock,
          measurable: rockSnapshot.measurable,
          due_date: rockSnapshot.due_date,
          status: rockSnapshot.status,
          progress: rockSnapshot.progress,
        },
        createdAt: now,
      };

      await addDoc(collection(db, COLLECTION_NAMES.ROCK_EVALUATIONS), evaluationData);
    } catch (error) {
      console.error('Error saving evaluation:', error);
      throw error;
    }
  }, [userId]);

  /**
   * Load company issues (issues not related to a rock)
   * Queries issues collection where relatedRockId is null
   */
  const loadCompanyIssues = useCallback(async (): Promise<Issue[]> => {
    try {
      // Firestore doesn't support querying for null directly, so we need to query all issues
      // and filter client-side, or use a different approach
      // For now, we'll query all issues and filter
      const issuesQuery = query(
        collection(db, COLLECTION_NAMES.ISSUES),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(issuesQuery);
      
      const issues = snapshot.docs
        .map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt || Timestamp.now(),
            updatedAt: data.updatedAt || Timestamp.now(),
            solvedAt: data.solvedAt?.toDate?.() || data.solvedAt,
            firstOpenedDate: data.firstOpenedDate || Timestamp.now(),
            createdInMeetingId: data.createdInMeetingId || undefined,
          } as Issue;
        })
        .filter((issue) => !issue.relatedRockId); // Filter for company issues (no relatedRockId)
      
      return issues;
    } catch (error) {
      console.error('Error loading company issues:', error);
      return [];
    }
  }, []);

  /**
   * Create a company issue (issue not related to a rock)
   */
  const createCompanyIssue = useCallback(async (
    name: string,
    description: string,
    assignedToUserId?: string,
    departmentId?: string
  ) => {
    if (!userId) throw new Error('User ID is required');

    try {
      const now = Timestamp.now();
      await addDoc(collection(db, COLLECTION_NAMES.ISSUES), {
        name,
        description,
        ownerId: userId,
        assignedToUserId: assignedToUserId || null,
        departmentId: departmentId || null,
        status: 'open',
        priority: 'medium',
        isRock: false,
        relatedRockId: null, // Company issue, not related to a rock
        firstOpenedDate: now,
        createdAt: now,
        updatedAt: now,
      });
    } catch (error) {
      console.error('Error creating company issue:', error);
      throw error;
    }
  }, [userId]);

  /**
   * Update a company issue
   */
  const updateCompanyIssue = useCallback(async (
    issueId: string,
    updates: Partial<{
      name: string;
      description: string;
      status: 'open' | 'in-progress' | 'solved';
      assignedToUserId: string | null;
      departmentId: string | null;
    }>
  ) => {
    try {
      const issueRef = doc(db, COLLECTION_NAMES.ISSUES, issueId);
      await updateDoc(issueRef, {
        ...updates,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error updating company issue:', error);
      throw error;
    }
  }, []);

  /**
   * Apply AI rewrite suggestions directly to a rock
   * Updates the rock with AI-suggested rock and measurable values
   * @param rockId - The ID of the rock to update
   * @param result - The AI evaluation result containing ai_rewrite_rock and ai_rewrite_measurable
   */
  const applyAIRewrite = useCallback(async (
    rockId: string,
    result: { ai_rewrite_rock: string; ai_rewrite_measurable: string }
  ): Promise<void> => {
    if (!rockId) {
      throw new Error('Rock ID is required');
    }

    if (!result?.ai_rewrite_rock || !result?.ai_rewrite_measurable) {
      throw new Error('AI rewrite suggestions are required');
    }

    try {
      const rockRef = doc(db, COLLECTION_NAMES.ROCKS, rockId);
      await updateDoc(rockRef, {
        rock: result.ai_rewrite_rock,
        measurable: result.ai_rewrite_measurable,
        updatedAt: Timestamp.now(),
        updatedByUserId: userId || null,
      });
    } catch (error) {
      console.error('Error applying AI rewrite:', error);
      throw error;
    }
  }, [userId]);

  /**
   * Apply rock suggestion updates (simulating application of AI suggestions)
   * Updates the rock with suggested title and measurable values
   * This function allows authenticated users to apply limited changes to rocks
   * (like AI suggestions to title or measurable), mimicking the broad RLS update intent.
   * @param rockId - The ID of the rock to update
   * @param updatedTitle - The suggested rock title
   * @param updatedMeasurable - The suggested measurable
   */
  const applyRockSuggestion = useCallback(async (
    rockId: string,
    updatedTitle: string,
    updatedMeasurable: string
  ): Promise<void> => {
    if (!rockId) {
      throw new Error('Rock ID is required');
    }

    if (!updatedTitle || !updatedMeasurable) {
      throw new Error('Updated title and measurable are required');
    }

    try {
      const rockRef = doc(db, COLLECTION_NAMES.ROCKS, rockId);
      await updateDoc(rockRef, {
        rock: updatedTitle,
        measurable: updatedMeasurable,
        updatedAt: Timestamp.now(),
        updatedByUserId: userId || null,
      });
    } catch (error) {
      console.error('Error applying rock suggestion:', error);
      throw error;
    }
  }, [userId]);

  /**
   * Acknowledge that the user has viewed the AI suggestions for a rock
   * Updates the latest evaluation document with acknowledgement timestamp and user ID
   * @param rockId - The ID of the rock whose suggestions were acknowledged
   */
  const acknowledgeSuggestion = useCallback(async (rockId: string): Promise<void> => {
    if (!userId || !rockId) {
      return; // Silently fail if no userId or rockId
    }

    try {
      // Get the latest evaluation for this rock
      const latestEvaluation = await getLatestEvaluation(rockId);
      
      if (!latestEvaluation) {
        console.warn('No evaluation found for rock:', rockId);
        return;
      }

      // Update the evaluation document with acknowledgement
      const evaluationRef = doc(db, COLLECTION_NAMES.ROCK_EVALUATIONS, latestEvaluation.id);
      await updateDoc(evaluationRef, {
        suggestionAcknowledgedAt: Timestamp.now(),
        acknowledgedBy: userId,
      });
    } catch (error) {
      console.error('Error acknowledging suggestion:', error);
      // Don't throw - this is a non-critical operation
    }
  }, [userId, getLatestEvaluation]);

  return {
    rocks,
    users: eosUsers,
    departments,
    loading,
    rockEvaluations,
    rockAiScores,
    createRock,
    updateRock,
    deleteRock,
    achieveRock,
    dropRock,
    loadMilestonesForRocks,
    loadActionItemsForRocks,
    loadIssuesForRocks,
    subscribeToIssues,
    subscribeToMilestonesForRock,
    subscribeToActionItemsForRock,
    subscribeToActionItems,
    addIssue,
    resolveIssue,
    actuallyResolveIssue,
    toggleActionItem,
    addActionItem,
    loadIssuesForRock,
    loadRockActionItemsForRock,
    loadActionItemsForRock,
    loadMilestonesForRock,
    addMilestone,
    updateMilestone,
    toggleMilestone,
    saveRockUpdate,
    loadPreviousNotes,
    checkAITerms,
    acceptAITerms,
    evaluateRock,
    getLatestEvaluation,
    saveEvaluation,
    loadCompanyIssues,
    createCompanyIssue,
    updateCompanyIssue,
    updateIssue,
    updateActionItem,
    applyAIRewrite,
    applyRockSuggestion,
    acknowledgeSuggestion,
  };
}

